import { DSIconName, getDSIcon } from "@Components/common/byb-ds/icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { Stack, SxProps, Theme } from "@mui/material";
import React from "react";

// Type for Font Awesome icons (keeping backwards compatibility)
export interface FontAwesomeIconConfig extends FontAwesomeIconProps {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  dsIcon?: never; // Ensure only one icon type is used
}

// Type for design system icons
export interface DSIconConfig {
  dsIcon: DSIconName;
  size?: number | string;
  color?: string;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  // Exclude FontAwesome props to prevent conflicts
  icon?: never;
}

export type AppIconProps = FontAwesomeIconConfig | DSIconConfig;

const DS_ICON_CLASS_NAME = "app-ds-icon";

// Common container styling
const iconContainerSx = (sx?: SxProps<Theme>, iconColor?: string) => ({
  alignSelf: "stretch",
  justifyContent: "center",
  ...(iconColor && {
    color: iconColor,
    [`& .${DS_ICON_CLASS_NAME} [stroke]:not([stroke='none'])`]: {
      stroke: iconColor,
    },
    [`& .${DS_ICON_CLASS_NAME} [fill]:not([fill='none'])`]: {
      fill: iconColor,
    },
  }),
  ...sx,
});

const Icon = (props: AppIconProps) => {
  const { sx, children, dsIcon, ...iconProps } = props;

  // Render design system icon
  if (dsIcon) {
    const DSIconComponent = getDSIcon(dsIcon);

    if (!DSIconComponent) {
      console.error(`Design system icon "${dsIcon}" not found in registry`);
      return null;
    }

    return (
      <Stack sx={iconContainerSx(sx, props.color || "currentColor")}>
        <DSIconComponent size={props.size || 18} color={props.color} {...iconProps} className={DS_ICON_CLASS_NAME} />
        {children}
      </Stack>
    );
  }

  // Render Font Awesome icon
  return (
    <Stack sx={iconContainerSx(sx)}>
      <FontAwesomeIcon {...(iconProps as FontAwesomeIconProps)} />
      {children}
    </Stack>
  );
};

export default Icon;
